import * as React from 'react';

import {
  CampaignAnalysisApiError,
  type CycleMedianStatusDto,
  type TargetPriceSourceDto,
  type TargetPriceSummaryRowDto,
} from '@/api/campaign-analysis-client';
import type { useTargetPriceSummary } from '@/api/use-campaign-analysis';
import { Button } from '@/components/ui/button';
import { Panel } from '@/components/ui/panel';
import { SENIORITY_LEVELS_ORDERED } from '@/lib/operational-view-labels';
import { formatEurOrDash, seniorityLabel } from '@/lib/rank-labels';
import { SortableHeader, type SortState } from '@/routes/admin/campaign-registry-format';

// =============================================================================
// target-price-summary-panel.tsx — Tableau définitif « Prix cible et médianes par cycle » (L9, DBU v2.7 §2.6).
// =============================================================================
// GATE_HUMAN John 2026-09-17 : « L'historique de la médiane, donc pour les cycles C1 et C2, ainsi que les prix
// cibles saisis par l'admin pour chaque couple profil/niveau d'expertise font partie du tableau définitif qui
// permet d'analyser les résultats de la campagne. » ; médianes « sur les seuls fournisseurs ayant soumis dans le
// cycle et avant négociation ».
//
// Lecture SEULE du read-model admin `GET /admin/positioning/:campaignId/target-price-summary` (useTargetPriceSummary),
// figé au calcul consolidé : aucun recalcul ici. Une ligne par couple du périmètre ; ordre par défaut = ordre API
// (famille, profil, niveau), tri client sur toutes les colonnes (aria-sort). Données NOMINATIVES potentielles
// (médiane à effectif 1) : écran admin uniquement. Tableau complet, indépendant des filtres de l'écran.
//
// Statut par cycle : « Sans objet » (C2 d'une campagne mono-cycle) est DISTINCT de « 0 réponse » (cycle existant
// sans soumission). Valeur absente ⇒ « — », jamais 0. 404 = pas encore d'analyse consolidée : état informatif.

type SummarySortKey =
  | 'default'
  | 'family'
  | 'profile'
  | 'level'
  | 'target'
  | 'source'
  | 'medianC1'
  | 'countC1'
  | 'medianC2'
  | 'countC2'
  | 'gap';

/** Libellés écran de l'origine du prix cible. */
export const TARGET_PRICE_SOURCE_SCREEN_LABELS: Readonly<Record<TargetPriceSourceDto, string>> = {
  PulseDefault: 'Proposé Pulse',
  AdminEntry: 'Saisi',
};

const LEVEL_RANK = new Map<string, number>(SENIORITY_LEVELS_ORDERED.map((l, i) => [l, i]));

/** Compare deux valeurs nullables : `null` TOUJOURS en fin de liste, quel que soit le sens. */
function compareNullable<T>(
  a: T | null,
  b: T | null,
  factor: number,
  compare: (x: T, y: T) => number,
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return factor * compare(a, b);
}

const byNumber = (x: number, y: number): number => x - y;
const byText = (x: string, y: string): number => x.localeCompare(y, 'fr');

/** Médiane triable d'un cycle : `null` si sans objet ou sans réponse. */
function sortableMedian(status: CycleMedianStatusDto, value: number | null): number | null {
  return status === 'Available' ? value : null;
}

function compareSummaryRows(
  a: TargetPriceSummaryRowDto,
  b: TargetPriceSummaryRowDto,
  { key, dir }: SortState<SummarySortKey>,
): number {
  const f = dir === 'asc' ? 1 : -1;
  switch (key) {
    case 'default':
      return 0;
    case 'family':
      return compareNullable(a.family, b.family, f, byText);
    case 'profile':
      return f * byText(a.profileCode, b.profileCode);
    case 'level':
      return f * ((LEVEL_RANK.get(a.seniorityLevel) ?? 99) - (LEVEL_RANK.get(b.seniorityLevel) ?? 99));
    case 'target':
      return compareNullable(a.targetPriceEur, b.targetPriceEur, f, byNumber);
    case 'source':
      return compareNullable(
        a.priceSource === null ? null : TARGET_PRICE_SOURCE_SCREEN_LABELS[a.priceSource],
        b.priceSource === null ? null : TARGET_PRICE_SOURCE_SCREEN_LABELS[b.priceSource],
        f,
        byText,
      );
    case 'medianC1':
      return compareNullable(
        sortableMedian(a.c1Status, a.medianC1Eur),
        sortableMedian(b.c1Status, b.medianC1Eur),
        f,
        byNumber,
      );
    case 'countC1':
      return f * (a.countC1 - b.countC1);
    case 'medianC2':
      return compareNullable(
        sortableMedian(a.c2Status, a.medianC2Eur),
        sortableMedian(b.c2Status, b.medianC2Eur),
        f,
        byNumber,
      );
    case 'countC2':
      return compareNullable(
        a.c2Status === 'NotApplicable' ? null : (a.countC2 ?? 0),
        b.c2Status === 'NotApplicable' ? null : (b.countC2 ?? 0),
        f,
        byNumber,
      );
    case 'gap':
      return compareNullable(a.gapMedianC2VsTargetEur, b.gapMedianC2VsTargetEur, f, byNumber);
  }
}

/** Écart signé « +50 € (+8 %) » ; « — » si indéfini. */
function formatGap(eur: number | null, pct: number | null): string {
  if (eur === null) {
    return '—';
  }
  const sign = (v: number): string => (v > 0 ? '+' : '');
  const eurPart = `${sign(eur)}${String(eur)} €`;
  return pct === null ? eurPart : `${eurPart} (${sign(pct)}${String(pct)} %)`;
}

/** Cellule médiane d'un cycle : « Sans objet », « 0 réponse » ou la valeur. */
function MedianCell({
  status,
  value,
}: {
  readonly status: CycleMedianStatusDto;
  readonly value: number | null;
}): React.JSX.Element {
  if (status === 'NotApplicable') {
    return <span className="text-muted-foreground">Sans objet</span>;
  }
  if (status === 'NoResponse' || value === null) {
    return <span className="text-muted-foreground">0 réponse</span>;
  }
  return <>{formatEurOrDash(value)}</>;
}

/** Cellule prix cible : valeur ou « — » doublé d'un libellé lu par les lecteurs d'écran. */
function TargetCell({ value }: { readonly value: number | null }): React.JSX.Element {
  if (value === null) {
    return (
      <>
        <span aria-hidden="true" className="text-muted-foreground">
          —
        </span>
        <span className="sr-only">aucun prix cible</span>
      </>
    );
  }
  return <>{formatEurOrDash(value)}</>;
}

/** Origine du prix ; valeur proposée en infobulle (et en texte lecteur d'écran) si elle diffère du prix. */
function SourceCell({ row }: { readonly row: TargetPriceSummaryRowDto }): React.JSX.Element {
  if (row.priceSource === null) {
    return (
      <>
        <span aria-hidden="true" className="text-muted-foreground">
          —
        </span>
        <span className="sr-only">aucune origine</span>
      </>
    );
  }
  const proposed =
    row.defaultEurProposed !== null && row.defaultEurProposed !== row.targetPriceEur
      ? `proposé : ${formatEurOrDash(row.defaultEurProposed)}`
      : undefined;
  return (
    <span title={proposed}>
      {TARGET_PRICE_SOURCE_SCREEN_LABELS[row.priceSource]}
      {proposed !== undefined ? <span className="sr-only">, {proposed}</span> : null}
    </span>
  );
}

const CELL = 'px-4 py-3.5 align-top';
const NUM_CELL = `${CELL} whitespace-nowrap text-right tabular-nums`;

export function TargetPriceSummaryPanel({
  query,
  onExportCsv,
  exportDisabled,
}: {
  readonly query: ReturnType<typeof useTargetPriceSummary>;
  /**
   * Export CSV DÉDIÉ de CE tableau (lot U1) : le classeur de la page ne porte plus que les deux
   * feuilles des cases cochées (décision 12), ce tableau garde donc son propre export, journalisé.
   * Le déclencheur vit dans la barre de titre de sa propre card.
   */
  readonly onExportCsv: () => void;
  readonly exportDisabled: boolean;
}): React.JSX.Element {
  const [sort, setSort] = React.useState<SortState<SummarySortKey>>({
    key: 'default',
    dir: 'asc',
  });
  const onSort = React.useCallback((key: SummarySortKey): void => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  }, []);

  const rows = React.useMemo(() => {
    const source = query.data?.rows ?? [];
    // Tri stable : à égalité, l'ordre API (famille, profil, niveau) est conservé.
    return sort.key === 'default' ? source : [...source].sort((a, b) => compareSummaryRows(a, b, sort));
  }, [query.data?.rows, sort]);

  const notYetAvailable =
    query.error instanceof CampaignAnalysisApiError && query.error.status === 404;
  const header = (key: SummarySortKey, label: string, className = 'pl-4'): React.JSX.Element => (
    <SortableHeader label={label} sortKey={key} sort={sort} onSort={onSort} className={className} />
  );

  return (
    <Panel
      headingId="analysis-target-price-summary-heading"
      title="Prix cible et médianes par cycle"
      count={query.data?.rows.length}
      aside={
        <>
          {query.data !== undefined ? (
            <span>
              Tableau définitif · calculé le{' '}
              <time dateTime={query.data.computedAt} className="tabular-nums">
                {query.data.computedAt.slice(0, 10)}
              </time>{' '}
              · hors filtres
            </span>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={exportDisabled}
            onClick={onExportCsv}
          >
            Exporter prix cible et médianes (CSV)
          </Button>
        </>
      }
    >
      {query.isPending ? (
        <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
          Chargement du tableau…
        </p>
      ) : notYetAvailable ? (
        <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
          Tableau disponible après la clôture définitive
        </p>
      ) : query.isError ? (
        <p role="alert" className="px-6 py-4 text-sm text-destructive">
          Erreur de chargement du tableau : {query.error.message}
        </p>
      ) : rows.length === 0 ? (
        <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
          Aucun couple dans le périmètre de la campagne.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="ds-table w-full border-collapse text-sm">
            <caption className="sr-only">
              Prix cible et médianes par cycle : pour chaque couple profil et niveau du périmètre, prix
              cible final et son origine, médianes des cycles 1 et 2 avant négociation avec leur effectif,
              écart de la médiane du cycle 2 au prix cible. Colonnes triables.
            </caption>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left">
                {header('family', 'Famille', 'pl-6')}
                {header('profile', 'Profil')}
                {header('level', 'Niveau')}
                {header('target', 'Prix cible', 'pl-4 text-right')}
                {header('source', 'Source')}
                {header('medianC1', 'Médiane C1', 'pl-4 text-right')}
                {header('countC1', 'n C1', 'pl-4 text-right')}
                {header('medianC2', 'Médiane C2', 'pl-4 text-right')}
                {header('countC2', 'n C2', 'pl-4 text-right')}
                {header('gap', 'Écart médiane C2 / prix cible', 'pl-4 !pr-6 text-right')}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${row.profileCode}:${row.seniorityLevel}`}
                  className="border-b border-border last:border-0"
                >
                  <td className={`${CELL} pl-6`}>
                    {row.family ?? (
                      <>
                        <span aria-hidden="true" className="text-muted-foreground">
                          —
                        </span>
                        <span className="sr-only">famille inconnue</span>
                      </>
                    )}
                  </td>
                  <th scope="row" className={`${CELL} text-left font-mono text-xs font-medium`}>
                    {row.profileCode}
                  </th>
                  <td className={CELL}>{seniorityLabel(row.seniorityLevel)}</td>
                  <td className={NUM_CELL}>
                    <TargetCell value={row.targetPriceEur} />
                  </td>
                  <td className={CELL}>
                    <SourceCell row={row} />
                  </td>
                  <td className={NUM_CELL}>
                    <MedianCell status={row.c1Status} value={row.medianC1Eur} />
                  </td>
                  <td className={NUM_CELL}>{row.countC1}</td>
                  <td className={NUM_CELL}>
                    <MedianCell status={row.c2Status} value={row.medianC2Eur} />
                  </td>
                  <td className={NUM_CELL}>
                    {row.c2Status === 'NotApplicable' ? (
                      <span className="text-muted-foreground">Sans objet</span>
                    ) : (
                      (row.countC2 ?? 0)
                    )}
                  </td>
                  <td className={`${NUM_CELL} pr-6`}>
                    {formatGap(row.gapMedianC2VsTargetEur, row.gapMedianC2VsTargetPct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
